import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { CheckCheck, X, ListChecks } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import StatusChip from "../../components/system/StatusChip";
import EmptyState from "../../components/system/EmptyState";
import Input, { Select } from "../../components/system/Input";

const TAB_TODAY   = "today";
const TAB_SEVEN   = "seven";
const TAB_HISTORY = "history";

const TYPE_OPTS = [
  { k: "all",                l: "All types" },
  { k: "expired_inventory",  l: "Expired Inventory" },
  { k: "manual",             l: "Manual" },
  { k: "product_request",    l: "Product Request" },
  { k: "suggestion_product_profile", l: "Suggestions Profile" },
];

const STATUS_OPTS = [
  { k: "open",     l: "Open" },
  { k: "resolved", l: "Resolved" },
  { k: "all",      l: "All" },
];

const dayMs = 24 * 60 * 60 * 1000;
const TODAY_CUTOFF_MS = () => Date.now() - dayMs;
const SEVEN_CUTOFF_MS = () => Date.now() - 7 * dayMs;

function ConfirmModal({ open, title, body, onCancel, onConfirm, confirmLabel = "Resolve" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" data-testid="review-confirm-modal">
      <Card variant="raised" className="w-full sm:max-w-md p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-display font-black text-xl tracking-tight">{title}</h3>
          <button onClick={onCancel} className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink" aria-label="Close" data-testid="review-confirm-cancel">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-ink/70 mb-5">{body}</p>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button tier="secondary" onClick={onCancel} data-testid="review-confirm-cancel-btn">Cancel</Button>
          <Button tier="primary" onClick={onConfirm} data-testid="review-confirm-ok">{confirmLabel}</Button>
        </div>
      </Card>
    </div>
  );
}

const splitCodes = (value) => value
  .split(",")
  .map((part) => part.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_"))
  .filter(Boolean);

function SuggestionProfileReview({ item, profile, onDone }) {
  const [category, setCategory] = useState("");
  const [family, setFamily] = useState("cheese");
  const [sweet, setSweet] = useState("");
  const [crisp, setCrisp] = useState("");
  const [savory, setSavory] = useState("");
  const [evidence, setEvidence] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    setBusy(true);
    try {
      if (decision === "reject") {
        await api.post(`/admin/suggestion-profile-reviews/${profile.id}/reject`);
        toast.success("Profile review rejected");
      } else {
        const pairings = {};
        if (splitCodes(sweet).length) pairings.sweet = splitCodes(sweet);
        if (splitCodes(crisp).length) pairings.crisp = splitCodes(crisp);
        if (splitCodes(savory).length) pairings.savory = splitCodes(savory);
        await api.post(`/admin/suggestion-profile-reviews/${profile.id}/approve`, {
          canonical_category: category,
          product_family: family,
          pairings,
          evidence: evidence.split("\n").map((line) => line.trim()).filter(Boolean),
          confidence: 1,
        });
        toast.success("Suggestions profile approved");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  if (!profile) {
    return (
      <div className="p-4 border-t border-border/40 first:border-t-0">
        <div className="font-mono text-xs uppercase tracking-wider">{item.type}</div>
        <div className="text-sm">{item.note}</div>
        <div className="text-xs text-danger mt-2">Profile record unavailable. Refresh this page.</div>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-border/40 first:border-t-0" data-testid={`suggestion-profile-review-${profile.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className="font-display font-black text-lg">{profile.product_name}</div>
          <div className="data-sku">
            Catalog: {profile.catalog_category || "Unknown"} · Barcode: {profile.barcode || "None"}
          </div>
        </div>
        <StatusChip tone="soon">Research needed</StatusChip>
      </div>
      <p className="text-sm text-ink-2 mb-4">
        Verify the product using trusted sources. Approval affects Suggestions only; it never changes the master catalog.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input value={category} onChange={(e) => setCategory(e.target.value.toUpperCase())} placeholder="Profile code, e.g. OSSAU_IRATY" />
        <Select value={family} onChange={(e) => setFamily(e.target.value)}>
          <option value="cheese">Cheese</option>
          <option value="cheese_spread">Cheese spread</option>
          <option value="sweet_accompaniment">Sweet accompaniment</option>
          <option value="crisp_accompaniment">Crisp accompaniment</option>
          <option value="savory_accompaniment">Savory accompaniment</option>
        </Select>
        <Input value={sweet} onChange={(e) => setSweet(e.target.value)} placeholder="Sweet codes, comma-separated" />
        <Input value={crisp} onChange={(e) => setCrisp(e.target.value)} placeholder="Crisp codes, comma-separated" />
        <Input value={savory} onChange={(e) => setSavory(e.target.value)} placeholder="Savory codes, comma-separated" />
        <textarea
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          placeholder={"Trusted evidence URLs\nOne https:// link per line"}
          className="min-h-24 rounded-button border border-border bg-paper px-3 py-2 text-sm"
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button tier="secondary" size="sm" disabled={busy} onClick={() => decide("reject")}>Reject</Button>
        <Button tier="primary" size="sm" disabled={busy} onClick={() => decide("approve")}>Approve profile</Button>
      </div>
    </div>
  );
}

export default function ReviewQueuePage() {
  const [items, setItems] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(TAB_TODAY);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [q, setQ] = useState("");

  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { mode: "selected" | "visible" }

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: profileData }] = await Promise.all([
        api.get("/review-queue"),
        api.get("/admin/suggestion-profile-reviews"),
      ]);
      setItems(Array.isArray(data) ? data : []);
      setProfiles(Array.isArray(profileData) ? profileData : []);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Reset selection whenever the visible window changes — keeps "select all
  // visible" intuitive: each tab/filter combo has its own working set.
  useEffect(() => { setSelected(new Set()); }, [tab, typeFilter, statusFilter, q]);

  /* ---------------- partitioning ---------------- */
  const { today, seven, history, todayCount, sevenCount, historyCount } = useMemo(() => {
    const tCut = TODAY_CUTOFF_MS();
    const sCut = SEVEN_CUTOFF_MS();
    const today = [], seven = [], history = [];
    for (const it of items) {
      const t = new Date(it.created_at).getTime();
      const fresh = Number.isFinite(t);
      // History bucket includes resolved items AND open items older than 7d.
      if (it.resolved || !fresh || t < sCut) {
        history.push(it);
      } else {
        seven.push(it);
        if (t >= tCut) today.push(it);
      }
    }
    return {
      today, seven, history,
      todayCount: today.length,
      sevenCount: seven.length,
      historyCount: history.length,
    };
  }, [items]);

  /* ---------------- filter pipeline ---------------- */
  const filtered = useMemo(() => {
    let base;
    if (tab === TAB_TODAY) base = today;
    else if (tab === TAB_SEVEN) base = seven;
    else base = history;

    return base.filter((it) => {
      if (typeFilter !== "all" && it.type !== typeFilter) return false;
      // Status filter — only meaningful in History (today/seven are open-only).
      if (tab === TAB_HISTORY) {
        if (statusFilter === "open" && it.resolved) return false;
        if (statusFilter === "resolved" && !it.resolved) return false;
      }
      if (q.trim()) {
        const needle = q.trim().toLowerCase();
        const hay = `${it.type || ""} ${it.note || ""} ${it.ref_id || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [tab, today, seven, history, typeFilter, statusFilter, q]);
  const profileById = useMemo(
    () => Object.fromEntries(profiles.map((profile) => [profile.id, profile])),
    [profiles],
  );

  /* ---------------- selection ---------------- */
  const visibleIds = useMemo(
    () => filtered
      .filter((it) => !it.resolved && it.type !== "suggestion_product_profile")
      .map((it) => it.id),
    [filtered],
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const next = new Set(selected);
      for (const id of visibleIds) next.delete(id);
      setSelected(next);
    } else {
      const next = new Set(selected);
      for (const id of visibleIds) next.add(id);
      setSelected(next);
    }
  };
  const clearSelection = () => setSelected(new Set());

  /* ---------------- single resolve ---------------- */
  const resolveOne = async (i) => {
    try {
      await api.patch(`/review-queue/${i.id}/resolve`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  /* ---------------- bulk resolve ---------------- */
  const filterContext = () => ({ tab, type: typeFilter, status: statusFilter, q: q.trim() || null });

  const submitBulk = async (mode) => {
    const ids = mode === "selected"
      ? Array.from(selected)
      : visibleIds;
    if (ids.length === 0) {
      toast.error("Nothing to resolve");
      setConfirm(null);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/review-queue/bulk-resolve", {
        ids,
        filter_context: filterContext(),
      });
      toast.success(
        `Resolved ${data.resolved_count} item${data.resolved_count === 1 ? "" : "s"}` +
        (data.already_resolved_count ? ` · ${data.already_resolved_count} already done` : "")
      );
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  /* ---------------- render ---------------- */
  const tabsMeta = [
    { k: TAB_TODAY,   l: "Today",         n: todayCount },
    { k: TAB_SEVEN,   l: "Last 7 days",   n: sevenCount },
    { k: TAB_HISTORY, l: "History",       n: historyCount },
  ];

  return (
    <div data-testid="admin-review-queue-page" className="pb-32">
      <PageHeader eyebrow="Exceptions" title="Review Queue" />

      {/* Tabs */}
      <div className="flex gap-2 mb-3 flex-wrap" data-testid="review-view-tabs">
        {tabsMeta.map(({ k, l, n }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={[
              "h-10 px-4 rounded-button font-display text-xs uppercase tracking-wide inline-flex items-center gap-2 transition-all duration-150 active:scale-[0.98]",
              tab === k
                ? "bg-ink text-white shadow-rest"
                : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
            ].join(" ")}
            data-testid={`review-view-${k}`}
          >
            {l}
            <span className={`font-mono text-[10px] ${tab === k ? "text-white/70" : "text-ink-3"}`}>{n}</span>
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4" data-testid="review-filters">
        <Input
          variant="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search type, note, or ref id…"
          className="flex-1 min-w-[200px]"
          data-testid="review-search-input"
        />
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="review-type-filter"
        >
          {TYPE_OPTS.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
        </Select>
        {tab === TAB_HISTORY && (
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="review-status-filter"
          >
            {STATUS_OPTS.map((o) => <option key={o.k} value={o.k}>{o.l}</option>)}
          </Select>
        )}
      </div>

      {/* Bulk action bar */}
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="review-bulk-bar">
        <Button
          tier="tertiary"
          size="sm"
          onClick={toggleSelectAllVisible}
          disabled={visibleIds.length === 0}
          data-testid="review-select-all-visible"
        >
          {allVisibleSelected ? "Deselect all visible" : `Select all visible (${visibleIds.length})`}
        </Button>
        <span className="text-xs text-ink-3 font-mono">
          {selected.size > 0 ? `${selected.size} selected` : "0 selected"}
        </span>
        <div className="flex-1" />
        {visibleIds.length > 0 && (
          <Button
            tier="secondary"
            size="sm"
            onClick={() => setConfirm({ mode: "visible" })}
            data-testid="review-resolve-all-visible-btn"
            disabled={busy}
          >
            <CheckCheck size={12} />
            Resolve all visible
          </Button>
        )}
      </div>

      <Card variant="raised" className="overflow-hidden" data-testid="review-list">
        {loading && filtered.length === 0 && (
          <div className="p-10 text-center font-display">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center font-display text-ink-2" data-testid="review-empty">
            {tab === TAB_TODAY    && "No open items in the last 24 hours."}
            {tab === TAB_SEVEN    && "No open items in the last 7 days."}
            {tab === TAB_HISTORY  && "No items in history match these filters."}
          </div>
        )}
        {filtered.map((i) => {
          if (i.type === "suggestion_product_profile" && !i.resolved) {
            return (
              <SuggestionProfileReview
                key={i.id}
                item={i}
                profile={profileById[i.ref_id]}
                onDone={load}
              />
            );
          }
          const isSel = selected.has(i.id);
          return (
            <label
              key={i.id}
              className={`flex items-center gap-3 p-4 cursor-pointer border-t border-border/40 first:border-t-0 ${isSel ? "bg-paper-sunk" : ""}`}
              data-testid={`review-row-${i.id}`}
            >
              <input
                type="checkbox"
                checked={isSel}
                disabled={i.resolved}
                onChange={() => toggleOne(i.id)}
                className="w-5 h-5 accent-brand mt-1 cursor-pointer shrink-0 disabled:opacity-30"
                data-testid={`review-checkbox-${i.id}`}
                aria-label={`Select review item ${i.id.slice(0, 8)}`}
              />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs uppercase tracking-wider">{i.type}</div>
                <div className="text-sm break-words">{i.note}</div>
                <div className="data-sku">{new Date(i.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <StatusChip tone={i.resolved ? "fresh" : "neutral"}>
                  {i.resolved ? "Resolved" : "Open"}
                </StatusChip>
                {!i.resolved && (
                  <Button
                    tier="secondary"
                    size="sm"
                    onClick={(e) => { e.preventDefault(); resolveOne(i); }}
                    data-testid={`resolve-${i.id}`}
                  >
                    Resolve
                  </Button>
                )}
              </div>
            </label>
          );
        })}
      </Card>

      {/* Sticky selection toolbar */}
      {selected.size > 0 && (
        <div
          className="fixed left-0 right-0 bottom-0 z-40 px-3 py-3 bg-ink text-paper shadow-[0_-4px_16px_rgba(0,0,0,0.18)]"
          role="region"
          aria-label="Review queue bulk actions"
          data-testid="review-selection-toolbar"
        >
          <div className="max-w-3xl mx-auto flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="h-11 w-11 flex items-center justify-center rounded-button hover:bg-paper/10 active:bg-paper/20"
              aria-label="Clear selection"
              data-testid="review-clear-selection"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
            <div className="font-mono text-[12px] uppercase tracking-wider mr-2 shrink-0">
              {selected.size} selected
            </div>
            <div className="flex-1 flex items-center justify-end">
              <button
                onClick={() => setConfirm({ mode: "selected" })}
                disabled={busy}
                className="h-11 px-4 rounded-button bg-paper text-ink font-display text-[12px] uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-40 transition-all duration-150 active:scale-[0.98]"
                data-testid="review-resolve-selected-btn"
              >
                <CheckCheck size={16} strokeWidth={2.5} />
                Resolve selected
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.mode === "visible" ? `Resolve all ${visibleIds.length} visible items?` : `Resolve ${selected.size} selected item${selected.size === 1 ? "" : "s"}?`}
        body="This will mark them resolved and log a single audit-history entry. Items are not deleted; they remain in the History tab."
        onCancel={() => setConfirm(null)}
        onConfirm={() => submitBulk(confirm?.mode || "selected")}
      />
    </div>
  );
}
